import {
  Button,
  ButtonMenu,
  ButtonVariant,
  column,
  dateColumn,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  MenuItem,
  multiFilter,
  numericColumn,
  RowStyles,
  simpleHeader,
  singleFilter,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { dateCell, markupCell, Percentage, priceCell, tagCell, yesNoCell } from "src/components";
import { getStage } from "src/context";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  ChangeEventFilter,
  ChangeEventStatus,
  ChangeEventType,
  CostConfidence,
  FeatureFlagType,
  Maybe,
  Stage,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useStripStage } from "src/hooks/useStripStage";
import { PageHeader } from "src/routes/layout/PageHeader";
import {
  ChangeEventsForProjectStage_ProjectItemFragment,
  ChangeEventsForProjectStageQuery,
  ChangeEventsPageChangeEventFragment,
  useChangeEventsForProjectStageQuery,
} from "src/routes/projects/change-events/ChangeEventsPage.generated";
import { CreateChangeEventModal } from "src/routes/projects/change-events/components/CreateChangeEventModal";
import { CreateGuidedChangeEventModal } from "src/routes/projects/change-events/components/CreateGuidedChangeEventModal";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ProjectParams } from "src/routes/routesDef";
import {
  createChangeEventUrl,
  createGuidedChangeEventUrl,
  createProjectProductOfferingAppliableConfigsUrl,
} from "src/RouteUrls";
import {
  calculateMarkup,
  changeEventStatusToNameMapper,
  changeEventStatusToTagTypeMapper,
  changeEventTypeToNameMapper,
  stageCodeToNameMapper,
} from "src/utils";

type ChangeEventsPageFilter = Omit<ChangeEventFilter, "stage"> & { stage?: Maybe<Stage> };

export function ChangeEventsPage() {
  useStripStage();
  const { projectId } = useRouteParams<ProjectParams>();
  const { clientNoun, latestActiveStage, shortClientNoun } = useProjectContext();

  const filterDefs: FilterDefs<ChangeEventsPageFilter> = useMemo(() => {
    const stages = [Stage.PreConExecution, Stage.Construction];
    const statuses = Object.values(ChangeEventStatus);

    const stage = singleFilter({
      options: [...stages.map((s) => ({ id: s, name: stageCodeToNameMapper[s] }))],
      defaultValue: stages.includes(latestActiveStage) ? latestActiveStage : undefined,
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const status = multiFilter({
      options: [
        ...statuses
          .filter((status) => getStage() !== "prod" || status !== ChangeEventStatus.PendingSignature)
          .map((s) => ({ id: s, name: changeEventStatusToNameMapper[s] })),
      ],
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const type = multiFilter({
      options: Object.entries(ChangeEventType),
      getOptionValue: ([_, type]) => type,
      getOptionLabel: ([_, type]) => `${clientNoun} ${changeEventTypeToNameMapper(type)}`,
    });

    return { stage, status, type };
  }, [clientNoun, latestActiveStage]);

  const layoutState = useGridTableLayoutState<ChangeEventsPageFilter>({
    persistedFilter: { filterDefs, storageKey: "changeEventsFilter" },
    search: "client",
  });
  const { filter } = layoutState;

  const query = useChangeEventsForProjectStageQuery({
    variables: {
      projectId,
      stagesFilter: filter.stage && { stage: [filter.stage] },
      changeEventFilter: mapToFilter(filter),
    },
  });

  const columns = useMemo(() => createColumns(clientNoun, shortClientNoun), [clientNoun, shortClientNoun]);
  const rowStyles = useMemo(() => createRowStyles(projectId), [projectId]);
  const createRows = useCallback((data: ChangeEventsForProjectStageQuery | undefined) => {
    const stages = data?.project.stages ?? [];
    return createChangeEventRows(
      stages.flatMap((s) => s.changeEvents),
      stages.flatMap((s) => s.projectItems),
    );
  }, []);

  return (
    <>
      <PageHeader
        title="Change Events"
        right={<CreateChangeEventAction projectId={projectId} label="Create Change Event" variant="primary" />}
      />
      <GridTableLayout
        layoutState={layoutState}
        hideEditColumns
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          sorting: { on: "client" },
          style: { rowHeight: "fixed", bordered: true },
          emptyFallback: "No Change Events Found",
          emptyState: {
            title: "No Change Events Created",
            description: "Create your first change event.",
            actions: <CreateChangeEventAction projectId={projectId} label="Create" variant="tertiary" />,
          },
        }}
      />
    </>
  );
}

type CreateChangeEventActionProps = { projectId: string; label: string; variant: ButtonVariant };

/** The create CTA, shared by the page header and the empty state. */
function CreateChangeEventAction(props: CreateChangeEventActionProps) {
  const { projectId, label, variant } = props;
  const { __typename } = useProjectContext();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const showGuidedChangeEvents = useFeatureFlag(FeatureFlagType.GuidedChangeEvent);

  const openCreateModal = () => openModal({ content: <CreateChangeEventModal projectId={projectId} /> });

  if (__typename !== "Structure") {
    return <Button label={label} variant={variant} onClick={openCreateModal} />;
  }

  const structureMenuItems: MenuItem[] = [
    ...(showGuidedChangeEvents
      ? [
          {
            label: "Change Event Workflow",
            onClick: () =>
              openModal({
                content: <CreateGuidedChangeEventModal projectId={projectId} useGuidedChangeEventFeatureFlag />,
                size: "xl",
              }),
          },
        ]
      : []),
    {
      label: "Change Configuration",
      onClick: () => navigate({ pathname: createProjectProductOfferingAppliableConfigsUrl(projectId) }),
    },
    { label: "All Other Changes", onClick: openCreateModal },
  ];

  return <ButtonMenu items={structureMenuItems} placement="right" trigger={{ label, variant }} />;
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ChangeEventsPageChangeEventFragment };
type TotalRow = { kind: "totals"; data: { markupPercentage: number; costInCents: number; priceInCents: number } };

type Row = HeaderRow | DataRow | TotalRow;

const createColumns = (clientNoun: string, shortClientNoun: string): GridColumn<Row>[] =>
  [
    column<Row>({
      header: "Name",
      data: ({ title, identifier }) => ({
        content: `CE #${identifier} - ${title}`,
        value: `CE #${identifier} - ${title}`,
        sortValue: identifier,
      }),
      w: 1,
      mw: "250px",
      totals: emptyCell,
      sticky: "left",
    }),
    column<Row>({
      header: "Stage",
      data: ({ projectStage }) => projectStage.stage.name,
      totals: emptyCell,
      w: "120px",
    }),
    dateColumn<Row>({ header: "Created", data: (data) => dateCell(data.createdAt), totals: emptyCell, w: "108px" }),
    dateColumn<Row>({ header: "Updated", data: (data) => dateCell(data.updatedAt), totals: emptyCell, w: "108px" }),
    column<Row>({
      header: `${clientNoun} CO?`,
      data: (data) => yesNoCell(data.type.code === ChangeEventType.External),
      totals: emptyCell,
      w: "146px",
    }),
    column<Row>({ header: "Impacted Budget", data: (data) => data.budgetPhase.name, totals: emptyCell, w: "146px" }),
    column<Row>({
      header: "LI Hard Bids",
      data: ({ lineItems }) => {
        const hardBidCount = lineItems.filter((li) => li.costConfidence === CostConfidence.HardBid).length;
        return `${hardBidCount}/${lineItems.length}`;
      },
      totals: emptyCell,
      w: "108px",
    }),
    numericColumn<Row>({
      header: "Cost",
      data: (data) => priceCell({ valueInCents: data.totalCostInCents }),
      totals: (totals) => priceCell({ valueInCents: totals.costInCents }),
      w: "100px",
    }),
    numericColumn<Row>({
      header: "Markup",
      data: (data) =>
        data.type.code !== ChangeEventType.Internal ? markupCell(data.totalCostInCents, data.totalPriceInCents) : "N/A",
      totals: (totals) => ({
        content: <Percentage percent={totals.markupPercentage} />,
        tooltip: "Calculate the original vs current markup",
      }),
      w: "164px",
    }),
    numericColumn<Row>({
      header: `Total ${shortClientNoun} Price`,
      data: (data) => priceCell({ valueInCents: data.totalPriceInCents }),
      totals: (totals) => priceCell({ valueInCents: totals.priceInCents }),
      w: "136px",
    }),
    column<Row>({
      header: "Status",
      data: ({ status }) => tagCell(changeEventStatusToTagTypeMapper[status], changeEventStatusToNameMapper[status]),
      totals: emptyCell,
      w: "100px",
      sticky: "right",
    }),
  ].compact();

function createChangeEventRows(
  changeEvents: ChangeEventsPageChangeEventFragment[],
  projectItems: ChangeEventsForProjectStage_ProjectItemFragment[],
): GridDataRow<Row>[] {
  if (changeEvents.isEmpty) return [];
  return [
    simpleHeader,
    ...changeEvents.map((ce) => ({ kind: "data" as const, id: ce.id, data: ce })),
    {
      kind: "totals",
      id: "totals",
      data: {
        costInCents: changeEvents.sum((ce) => ce.totalCostInCents),
        markupPercentage: calculateMarkupPercentage(changeEvents, projectItems),
        priceInCents: changeEvents.sum((ce) => ce.totalPriceInCents),
      },
    },
  ];
}

function createRowStyles(projectId: string): RowStyles<Row> {
  return { header: {}, data: { rowLink: (ce) => createRowLink(projectId, ce.data) } };
}

function createRowLink(projectId: string, changeEvent: ChangeEventsPageChangeEventFragment) {
  const { id, status, workflow } = changeEvent;
  const isGuidedDraft = status === ChangeEventStatus.Draft && !!workflow;
  return isGuidedDraft ? createGuidedChangeEventUrl(projectId, id) : createChangeEventUrl(projectId, id);
}

/** Calculate markup looking up the original cost/price of the project */
function calculateMarkupPercentage(
  changeEvents: ChangeEventsPageChangeEventFragment[],
  projectItems: ChangeEventsForProjectStage_ProjectItemFragment[],
) {
  const nonInternalAccepted = changeEvents.filter(
    (ce) => ce.type.code !== ChangeEventType.Internal && ce.status === ChangeEventStatus.Accepted,
  );
  // Sum up the total cost/price of all accepted change events
  const changedBudgetInCents = nonInternalAccepted.sum((ce) => ce.totalCostInCents);
  const changedPriceInCents = nonInternalAccepted.sum((ce) => ce.totalPriceInCents);

  // Sum up the original cost/price of all project items
  const originalBudgetInCents = projectItems.sum((pi) => pi.originalBudgetInCents);
  const originalPriceInCents = projectItems.sum((pi) => pi.originalPriceInCents);

  // Calculate the current cost/price of the project (original + changed)
  const currentBudgetInCents = originalBudgetInCents + changedBudgetInCents;
  const currentPriceInCents = originalPriceInCents + changedPriceInCents;

  // Calculate the markup percentage for the original and current project
  const { markupPercentage: original } = calculateMarkup(originalBudgetInCents, originalPriceInCents);
  const { markupPercentage: current } = calculateMarkup(currentBudgetInCents, currentPriceInCents);

  // Return the difference between the current and original markup
  return current - original;
}

function mapToFilter(filter: ChangeEventsPageFilter): ChangeEventFilter {
  const { stage, status, type, ...rest } = filter;
  // Filter out void status by default
  const statusFilter = filter.status ?? Object.values(ChangeEventStatus).filter((s) => s !== ChangeEventStatus.Void);
  return { ...rest, ...{ status: statusFilter }, ...(filter.type && { type: filter.type }) };
}
